import React, { useState, useEffect } from 'react';
import { navigate } from '@reach/router';
import axios, { AxiosError } from 'axios';
import { isMobile } from 'react-device-detect';
import fileDownload from 'js-file-download';
import { FunctionComponentReturnType } from 'src/types/sharedReact';
import RouteBuilder from 'src/utils/routing/RouteBuilder';
import { useRouteBuilder } from 'src/utils/routing/RouteBuilderContext';
import { useAuthenticationContext, AuthenticationContextState } from 'src/contextProviders/AuthenticationProvider/AuthenticationContext';
import { Dashboard, DashboardType } from 'src/types/dashboard';
import { ErrorDetailsResponse, GetDashboardsResponse, GetReportCategoriesResponse, GetReportsResponse } from 'src/types/apiResponse';
import { Partner } from 'src/types/partner';
import { Report } from 'src/types/report';
import { getFromApi } from 'src/utils/getFromApi';
import { convertArrayBufferToJson } from 'src/utils/typeConversions';
import { extractFilename } from 'src/utils/contentDisposition';
import { getPartnerColor } from 'src/utils/partnerColors';
import { formatDate, parseDate, isReportInDateRange } from 'src/utils/datetime';
import { USE_MOCK_REPORTS_DATA, getMockReportCategoriesResponse, getMockReportsResponse } from 'src/mocks/reportsMockData';
import { DateTime } from 'luxon';
import ReportsDateSelector from 'src/components/ReportsDateSelector/ReportsDateSelector';
import DropdownSelect from 'src/components/formElements/DropdownSelect/DropdownSelect';
import DashboardCard from 'src/components/DashboardCard/DashboardCard';
import PartnerSwitchModal from 'src/components/PartnerSwitchModal';
import DashboardModal from 'src/components/modals/DashboardModal/DashboardModal';
import ViewReportModal from 'src/components/modals/ViewReportModal/ViewReportModal';
import Form from 'src/components/formElements/Form/Form';
import FormWithReCaptcha from 'src/components/formElements/Form/FormWithReCaptcha';
import SortableColumnHeader from 'src/components/tables/SortableColumnHeader/SortableColumnHeader';
import analyticsIcon from 'src/assets/images/AnalyticsIcon.png';
import { isUserAuthorizedToSeeMenuItem } from 'src/utils/userAuthorizationUtils';
import { pageMetadatas } from 'src/utils/routing/pageMetadatas';
import { CLIENT_PATHS } from 'src/utils/routing/routes';

import '@cof/dxo-ui-button';
import '@cof/dxo-ui-text-banner';
import '@cof/dxo-ui-table';
import './HomePageV2.css';
import useGetScreenInfo, { screenInfo, ScreenType } from 'src/utils/responsive';
import { Button, Checkbox, Spinner } from '@c1/gravity-react';
import CardLogo from 'src/components/SelectableCard/CardLogo';
import NotAuthorizedPage from 'src/pages/NotAuthorizedPage/NotAuthorizedPage';

// ====== CONSTANTS ======
const SORT_OPTIONS = [
    { label: "Name", value: "ALPHABETICAL" },
    { label: "Most Viewed", value: "MV_MONTH" }
];

const ALL_CATEGORIES_CODE = "ALL";

// Partners that don't have any reports available — skip the Reports section entirely.
const PARTNERS_WITHOUT_REPORTS = ['BJS', 'REI'];

// Maximum number of reports the API will accept in a single batch download.
// Mirrors the legacy ReportsTable cap so the UI matches backend constraints.
const MAX_NUMBER_OF_REPORTS = 3;

// Reports tagged with their owning category so "All" mode can mix them
type DisplayReport = Report & { categoryCode: string; categoryName: string };

/**
 * HomePageV2 - Unified Analytics Dashboard
 *
 * Displays dashboards and reports for the selected partner in a single consolidated view.
 *
 * Key Features:
 * - Partner selection with dynamic color theming
 * - Dashboard browsing with sorting options
 * - Report categories with file downloads
 * - Date-based report filtering
 * - Mobile responsive layout
 *
 * Data Flow:
 * 1. Partner selected → Fetch dashboards + report categories
 * 2. Category auto-selected → Fetch reports for that category
 * 3. User can change sort/filter/date selections
 *
 * Effects (5 total):
 * 1. Authentication check & redirect guard
 * 2. First partner redirect (if no partner in URL)
 * 3. Dashboard fetch (with sort handling)
 * 4. Report categories fetch (with auto-select)
 * 5. Reports fetch (with date initialization)
 */
function HomePageV2(): FunctionComponentReturnType {

    // ====== CONTEXT & HOOKS ======
    const routeBuilder: RouteBuilder = useRouteBuilder();
    const { isAuthenticated, user }: AuthenticationContextState = useAuthenticationContext();
    const screenInfo: screenInfo = useGetScreenInfo();
    const isDesktop = !isMobile;

    // ====== RESPONSIVE DASHBOARD LIMIT ======
    const MAX_CARDS_BEFORE_VIEW_MORE = 6;

    // Calculate how many cards fit in one row based on content width
    const getCardsPerRow = (): number => {
        if (isMobile) return 2;

        const contentWidth = Math.min(window.innerWidth * 0.78, 1332); // cap at 1332px
        const cardMinWidth = 180;
        const gap = 40;
        // auto-fill adds a column when there's room for another cardMinWidth + gap
        return Math.floor((contentWidth + gap) / (cardMinWidth + gap));
    };

    const [cardsPerRow, setCardsPerRow] = useState<number>(getCardsPerRow());

    // Update cards per row on window resize
    useEffect(() => {
        const handleResize = () => {
            setCardsPerRow(getCardsPerRow());
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // ====== STATE ======
    const [hasCheckedAuthentication, setHasCheckedAuthentication] = useState<boolean>(false);

    // ====== ACCESS CONTROL ======
    const hasAccessToReports = user?.partnerAccess
        ? isUserAuthorizedToSeeMenuItem(user, { metadata: pageMetadatas[CLIENT_PATHS.SELECT_PARTNER_PAGE] })
        : false;
    const hasAccessToDashboards = user?.partnerAccess
        ? isUserAuthorizedToSeeMenuItem(user, { metadata: pageMetadatas[CLIENT_PATHS.DASHBOARDS_SELECT_PARTNER_PAGE] })
        : false;

    // ====== AUTHENTICATION ======
    React.useEffect(() => {
        if (!user?.partnerAccess || !isAuthenticated) return;
        setHasCheckedAuthentication(true);
    }, [user, isAuthenticated]);

    // ====== PARTNER STATE ======
    const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);

    // ====== DERIVED STATE ======
    // Derive available partners from user context (no Effect needed)
    const availablePartners = React.useMemo(() => {
        if (!user?.partnerAccess?.partners) return [];
        const partners = user.partnerAccess.partners;
        const extPartners = partners['EXT'] || [];
        const intPartners = partners['INT'] || [];
        return [...extPartners, ...intPartners];
    }, [user?.partnerAccess?.partners]);

    // Auto-select first partner when available (or if selected partner is no longer valid)
    React.useEffect(() => {
        if (availablePartners.length > 0 && !selectedPartner) {
            setSelectedPartner(availablePartners[0]);
        }
    }, [availablePartners, selectedPartner]);

    // ====== COMPONENT STATE ======
    const [showAllDashboards, setShowAllDashboards] = useState<boolean>(false);
    const [data, setData] = useState<GetDashboardsResponse | null>(null);
    const [error, setError] = useState<AxiosError | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [sortOption, setSortOption] = useState<string>("MV_MONTH");
    const [sortLoading, setSortLoading] = useState<boolean>(false);

    const [showPartnerModal, setShowPartnerModal] = useState<boolean>(false);

    const [reportCategoriesData, setReportCategoriesData] = useState<GetReportCategoriesResponse | null>(null);
    const [reportCategoriesError, setReportCategoriesError] = useState<AxiosError | null>(null);
    const [isLoadingReports, setIsLoadingReports] = useState<boolean>(true);
    // Per-category cache of fetched reports — keyed by category code, value is the
    // tagged report list. "ALL" mode reads every entry; specific category reads one.
    const [reportsByCategory, setReportsByCategory] = useState<Record<string, DisplayReport[]>>({});
    const [reportsError, setReportsError] = useState<AxiosError | null>(null);
    const [selectedCategoryCode, setSelectedCategoryCode] = useState<string>(ALL_CATEGORIES_CODE);
    const [reportsTableLoading, setReportsTableLoading] = useState<boolean>(false);

    // ====== TABLE INTERACTION STATE (selection / sort / batch download) ======
    // Mirrors legacy ReportsTable.tsx so the table behaves identically.
    const [selectedReports, setSelectedReports] = useState<DisplayReport[]>([]);
    const [sortBy, setSortBy] = useState<keyof DisplayReport | null>(null);
    const [sortDirection, setSortDirection] = useState<number>(0);
    const [isDownloadInProgress, setIsDownloadInProgress] = useState<boolean>(false);
    const [downloadError, setDownloadError] = useState<string | null>(null);
    const [modalDashboard, setModalDashboard] = useState<Dashboard | null>(null);
    const [viewingReport, setViewingReport] = useState<Report | null>(null);
    const [reportStartDate, setReportStartDate] = useState<DateTime | null>(null);
    const [reportEndDate, setReportEndDate] = useState<DateTime | null>(null);


    // ====== API ROUTES ======
    const currentPartnerCode = selectedPartner?.code;
    const currentAccessType = selectedPartner?.type;
    const partnerHasReports = currentPartnerCode != null && !PARTNERS_WITHOUT_REPORTS.includes(currentPartnerCode);

    // Build the API route to fetch report categories for selected partner
    const reportCategoriesApiRoute = selectedPartner ? routeBuilder.api.getReportCategoriesForPartner({
        accessTypeCode: currentAccessType,
        partnerCode: currentPartnerCode
    }) : null;

    // ====== DASHBOARD DATA FETCHING ======
    // Runs concurrently with the report-categories effect below — both fire on
    // partner change in the same render commit, so requests overlap on the network.
    useEffect(() => {
        if (!selectedPartner) return;

        if (!data) setIsLoading(true);
        setSortLoading(true);

        const dashboardApiRoute = sortOption === 'MV_MONTH'
            ? routeBuilder.api.getMostViewedDashboardsPastMonthForPartner({
                accessTypeCode: currentAccessType,
                partnerCode: currentPartnerCode
            })
            : routeBuilder.api.getDashboardsForPartner({
                accessTypeCode: currentAccessType,
                partnerCode: currentPartnerCode
            });

        return getFromApi<GetDashboardsResponse>(
            dashboardApiRoute,
            (data) => {
                setData(data);
                setError(null);
                // If MV_MONTH came back empty, the fallback effect below will swap
                // sortOption to ALPHABETICAL and refire this fetch. Keep the loading
                // flags ON during that handoff so the UI doesn't briefly flash a
                // "no dashboards" empty state between the two responses.
                const willFallbackToAlphabetical =
                    sortOption === 'MV_MONTH' && data.dashboards.length === 0;
                if (!willFallbackToAlphabetical) {
                    setIsLoading(false);
                    setSortLoading(false);
                }
            },
            (error) => {
                if (error == null) return;
                setError(error);
                setIsLoading(false);
                setSortLoading(false);
            }
        )();
        // routeBuilder intentionally omitted: PageTemplate constructs a new
        // RouteBuilder on every render, which would otherwise re-fire this
        // effect on every parent render and cancel in-flight fetches mid-flight
        // (the response would come back over the wire but never reach state).
        // The closure value is fine because routeBuilder.api.get*() only reads
        // its explicit args, not the builder's internal state.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPartner, currentAccessType, currentPartnerCode, sortOption]);

    // Handle dashboard data response — mirrors legacy behavior:
    // If no dashboards returned and we're on MV_MONTH, fall back to ALPHABETICAL
    // (the success handler keeps loading flags ON so the spinner persists across
    // the fallback fetch). Otherwise, stop loading.
    useEffect(() => {
        if (!data) return;
        if (!data.dashboards.length && sortOption === 'MV_MONTH') {
            handleSort("ALPHABETICAL");
            return;
        }
        setSortLoading(false);
        // sortOption included so we stop loading when ALPHABETICAL eventually returns.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, sortOption]);

    function handleSort(option: string) {
        if (!selectedPartner) return;
        setSortLoading(true);
        setSortOption(option);
    }

    // ====== REPORTS DATA FETCHING ======
    /**
     * Effect #1: Fetch available report categories for the selected partner
     * - Triggered by: partner change
     * - Returns: List of category options (e.g., "Account", "Application", "Balance threshold")
     * - Default category selection is ALL_CATEGORIES_CODE; the reports effect below
     *   handles fanning out fetches for "All" mode.
     * - Dependencies: selectedPartner, reportCategoriesApiRoute, currentPartnerCode, currentAccessType
     */
    useEffect(() => {
        if (!selectedPartner) return;

        if (!partnerHasReports) {
            setIsLoadingReports(false);
            return;
        }

        setIsLoadingReports(true);

        if (USE_MOCK_REPORTS_DATA) {
            console.log('Using mock report categories data');
            const mockData = getMockReportCategoriesResponse(currentPartnerCode || '', currentAccessType || '');
            setReportCategoriesData(mockData);
            setReportCategoriesError(null);
            setIsLoadingReports(false);

            // Log data
            console.log('Available report categories for partner:', {
                partner: mockData.selectedPartner,
                accessType: mockData.selectedAccessType,
                categories: mockData.categories
            });
        } else if (reportCategoriesApiRoute) {
            console.log('Fetching report categories from:', reportCategoriesApiRoute);
            getFromApi<GetReportCategoriesResponse>(
                reportCategoriesApiRoute,
                (data) => {
                    setReportCategoriesData(data);
                    setReportCategoriesError(null);
                    setIsLoadingReports(false);

                    // Log data immediately after it arrives
                    console.log('Available report categories for partner:', {
                        partner: data.selectedPartner,
                        accessType: data.selectedAccessType,
                        categories: data.categories
                    });
                },
                (error) => {
                    // getFromApi calls this with `null` after every successful fetch
                    // to clear error state — bail before doing anything destructive.
                    if (error == null) return;
                    setReportCategoriesError(error);
                    setIsLoadingReports(false);

                    // Log error immediately
                    const errorStatus = error.response?.status;
                    console.error('Error fetching report categories:', {
                        status: errorStatus,
                        statusText: error.response?.statusText,
                        data: error.response?.data,
                        url: error.config?.url
                    });

                    if (errorStatus === 400) {
                        console.info('Partner may not have report categories available or user lacks access');
                    }
                }
            )();
        }
    }, [selectedPartner, reportCategoriesApiRoute, currentPartnerCode, currentAccessType, partnerHasReports]);

    /**
     * Effect #2: Fetch reports for the selected category (or every category in "All" mode)
     * - Triggered by: category selection change, partner change, or categories list update
     * - In "All" mode: fans out fetches for every category not already cached
     * - In specific category mode: fetches that one category if not cached
     * - Each report is tagged with its categoryCode/categoryName so "All" can mix them
     * - Per-category errors are logged but don't fail the whole table; the failed
     *   category is cached as an empty list so we don't retry on the same partner
     * - Date filtering happens client-side, so this effect does NOT depend on reportStartDate
     */
    useEffect(() => {
        if (!selectedPartner) return;
        const categories = reportCategoriesData?.categories;
        if (!categories || categories.length === 0) return;

        const targets = selectedCategoryCode === ALL_CATEGORIES_CODE
            ? categories
            : categories.filter((c) => c.code === selectedCategoryCode);

        // Lazy: only fetch categories whose reports we don't already have cached
        const toFetch = targets.filter((c) => !(c.code in reportsByCategory));

        if (toFetch.length === 0) {
            setReportsTableLoading(false);
            return;
        }

        setReportsTableLoading(true);

        if (USE_MOCK_REPORTS_DATA) {
            const updates: Record<string, DisplayReport[]> = {};
            toFetch.forEach((category) => {
                const mockData = getMockReportsResponse(category.code, currentPartnerCode || '', currentAccessType || '');
                const flat = Object.values(mockData.reports || {}).flat();
                updates[category.code] = flat.map((r) => ({
                    ...r,
                    categoryCode: category.code,
                    categoryName: category.name,
                }));
            });
            setReportsByCategory((prev) => ({ ...prev, ...updates }));
            setReportsTableLoading(false);
            return;
        }

        let pendingCount = toFetch.length;
        const cleanups: Array<() => void> = [];

        toFetch.forEach((category) => {
            const route = routeBuilder.api.getReports({
                accessTypeCode: currentAccessType,
                partnerCode: currentPartnerCode,
                reportCategoryCode: category.code,
            });

            const destructor = getFromApi<GetReportsResponse>(
                route,
                (data) => {
                    const flat = Object.values(data.reports || {}).flat();
                    const tagged: DisplayReport[] = flat.map((r) => ({
                        ...r,
                        categoryCode: category.code,
                        categoryName: category.name,
                    }));
                    setReportsByCategory((prev) => ({ ...prev, [category.code]: tagged }));
                    setReportsError(null);
                    pendingCount -= 1;
                    if (pendingCount === 0) setReportsTableLoading(false);
                },
                (error) => {
                    // getFromApi calls this with `null` after every successful fetch
                    // to clear error state — that's not a real failure, so bail out
                    // before any cache/loading bookkeeping (the success handler above
                    // has already done that work).
                    if (error == null) return;
                    console.warn(`Failed to fetch reports for category ${category.code}:`, error);
                    // Cache empty so we don't retry while on the same partner
                    setReportsByCategory((prev) => ({ ...prev, [category.code]: [] }));
                    // Only surface the error in single-category mode where the user is
                    // explicitly asking for that one. In "All" mode, log and continue.
                    if (selectedCategoryCode !== ALL_CATEGORIES_CODE) {
                        setReportsError(error);
                    }
                    pendingCount -= 1;
                    if (pendingCount === 0) setReportsTableLoading(false);
                }
            )();
            if (typeof destructor === 'function') {
                cleanups.push(destructor);
            }
        });

        return () => cleanups.forEach((c) => c());
        // reportsByCategory is intentionally omitted: the closure snapshot is enough
        // for the "is this cached?" check, and including it would re-fire the effect
        // on every cache update.
        // routeBuilder is also intentionally omitted: PageTemplate constructs a new
        // RouteBuilder on every render, which would otherwise re-fire this effect on
        // every parent render and cancel in-flight fetches mid-flight (causing the
        // response to come back over the wire but never reach state). The closure
        // value is fine because routeBuilder.api.getReports() only reads its explicit
        // args, not the builder's internal state.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPartner, selectedCategoryCode, reportCategoriesData, currentPartnerCode, currentAccessType]);

    // ====== COMPUTED VALUES (REPORTS) ======
    /**
     * Reports for the current view, derived from the per-category cache.
     * - "All": flatten every cached entry, dedup by report id (in case the same
     *   report somehow appears in multiple categories)
     * - Specific: read just that category's slot
     */
    const allReports: DisplayReport[] = React.useMemo(() => {
        if (selectedCategoryCode === ALL_CATEGORIES_CODE) {
            const seen = new Set<string>();
            const merged: DisplayReport[] = [];
            Object.values(reportsByCategory).forEach((arr) => {
                arr.forEach((r) => {
                    if (!seen.has(r.id)) {
                        seen.add(r.id);
                        merged.push(r);
                    }
                });
            });
            return merged;
        }
        return reportsByCategory[selectedCategoryCode] || [];
    }, [reportsByCategory, selectedCategoryCode]);

    /**
     * Initialize the date filter once reports are loaded.
     * - Defers until in-flight fetches finish so "All" mode picks the latest
     *   date across every category, not just whichever returned first.
     * - Lives in its own effect so changing the date filter doesn't refetch reports.
     */
    useEffect(() => {
        if (reportStartDate || reportsTableLoading) return;
        if (allReports.length === 0) return;
        const latest = allReports.reduce((latest, report) =>
            report.reportDate > latest.reportDate ? report : latest
        );
        const date = parseDate(latest.reportDate);
        setReportStartDate(date);
        setReportEndDate(date);
    }, [allReports, reportStartDate, reportsTableLoading]);

    // ====== EVENT HANDLERS ======
    const handleDashboardClick = (dashboard: Dashboard) => {
        if (dashboard.dashboardType === DashboardType.QUICKSIGHT) {
            window.scrollTo(0, 0);
            navigate(routeBuilder.client.toQuicksightPageV2({
                dashboardId: dashboard.dashboardId
            }));
        } else {
            // For non-Quicksight dashboards (Tableau, etc.), open in modal
            setModalDashboard(dashboard);
        }
    };

    const handlePartnerSwitch = (partner: Partner) => {
        setSelectedPartner(partner);
        setShowAllDashboards(false);
        setSelectedCategoryCode(ALL_CATEGORIES_CODE);
        setReportsByCategory({});
        setReportStartDate(null);
        setReportEndDate(null);
    };

    const handleDownloadReport = (reportId: string) => {
        const downloadUrl = routeBuilder.api.downloadReports({ reportIds: [reportId] });
        window.open(downloadUrl, '_blank');
    };

    // ====== TABLE INTERACTION HELPERS ======
    const isReportSelected = (report: DisplayReport): boolean =>
        Boolean(selectedReports.find((r) => r.id === report.id));

    const toggleReportSelected = (report: DisplayReport): void => {
        setSelectedReports((prev) =>
            prev.find((r) => r.id === report.id)
                ? prev.filter((r) => r.id !== report.id)
                : prev.concat(report)
        );
    };

    const isMaxNumberOfReportsSelected = selectedReports.length >= MAX_NUMBER_OF_REPORTS;

    /**
     * Batch download — copied from legacy ReportsTable.tsx.
     * - Bails on empty selection or selections > MAX_NUMBER_OF_REPORTS (API cap).
     * - Requests an arraybuffer (zip/binary), reads the filename out of the
     *   Content-Disposition header, and triggers a save dialog via
     *   js-file-download.
     * - On error, decodes the JSON body that arrives as an arraybuffer and
     *   surfaces the message inline above the table.
     */
    const downloadSelectedReports = async (): Promise<void> => {
        if (!selectedReports.length || selectedReports.length > MAX_NUMBER_OF_REPORTS) {
            return;
        }
        setIsDownloadInProgress(true);
        setDownloadError(null);
        try {
            const res = await axios.get<ArrayBuffer>(
                routeBuilder.api.downloadReports({ reportIds: selectedReports.map((r) => r.id) }),
                { responseType: 'arraybuffer' }
            );
            const fileName = extractFilename(res.headers['content-disposition'], 'reports.zip');
            fileDownload(res.data, fileName);
        } catch (e) {
            const err = e as AxiosError;
            const decoded = err.response?.data
                ? convertArrayBufferToJson<ErrorDetailsResponse>(err.response.data as ArrayBuffer)
                : null;
            setDownloadError(decoded?.message || 'Failed to download reports.');
        } finally {
            setIsDownloadInProgress(false);
        }
    };

    const partnerColor = getPartnerColor(currentPartnerCode);

    if (error || reportsError) {
        // Don't throw report categories error, just log it
        if (reportCategoriesError) {
            console.warn('Report categories failed to load, but continuing with dashboards');
        }
        if (reportsError) {
            console.warn('Reports failed to load, but continuing');
        }
        if (error) {
            throw error;
        }
    }

    // Get dashboards from API response, fallback to empty array
    const dashboards = data?.dashboards || [];
    const currentDashboardLimit = isDesktop ? Math.min(cardsPerRow, MAX_CARDS_BEFORE_VIEW_MORE) : MAX_CARDS_BEFORE_VIEW_MORE;
    const visibleDashboards = showAllDashboards
        ? dashboards
        : dashboards.slice(0, currentDashboardLimit);
    const partnerName = data?.partner?.name || 'this partner';

    // Get report categories from API response, fallback to empty array
    const reportCategories = reportCategoriesData?.categories || [];

    // ====== COMPUTED VALUES ======
    /**
     * Reports to display in the table.
     * - "All" mode: for each category, show a single report from its latest
     *   reportDate (the one most recently modified). Categories like the
     *   realtime dialer (DIAL) emit hourly files that share a reportDate, so
     *   listing every match floods the view; collapsing to one keeps the row
     *   count manageable while still surfacing the freshest file.
     * - Specific category: filter by the user's selected date range.
     * - When the user clicks a sortable column header, sort by that column;
     *   otherwise default to reportDate descending.
     */
    const recentReports: DisplayReport[] = React.useMemo(() => {
        let result: DisplayReport[];
        if (selectedCategoryCode === ALL_CATEGORIES_CODE) {
            result = [];
            Object.values(reportsByCategory).forEach((categoryReports) => {
                if (categoryReports.length === 0) return;
                const latestDate = categoryReports.reduce(
                    (max, r) => (r.reportDate > max ? r.reportDate : max),
                    categoryReports[0].reportDate
                );
                const latestReport = categoryReports.reduce<DisplayReport | null>((best, r) => {
                    if (r.reportDate !== latestDate) return best;
                    if (!best || r.lastModified > best.lastModified) return r;
                    return best;
                }, null);
                if (latestReport) result.push(latestReport);
            });
        } else {
            result = reportStartDate && reportEndDate
                ? allReports.filter((r) => isReportInDateRange(r, reportStartDate, reportEndDate))
                : allReports.slice();
        }
        if (sortBy && sortDirection !== 0) {
            // Comparator copied from legacy ReportsTable: numeric subtraction for
            // numbers, locale-aware case-insensitive compare for strings.
            result.sort((a, b) => {
                const fieldA = a[sortBy];
                const fieldB = b[sortBy];
                let diff = 0;
                if (typeof fieldA === 'number' && typeof fieldB === 'number') {
                    diff = fieldA - fieldB;
                }
                if (typeof fieldA === 'string' && typeof fieldB === 'string') {
                    diff = fieldA.toLocaleLowerCase().localeCompare(fieldB.toLocaleLowerCase());
                }
                return diff * sortDirection;
            });
        } else {
            result.sort((a, b) => b.reportDate - a.reportDate);
        }
        return result;
    }, [selectedCategoryCode, reportsByCategory, allReports, reportStartDate, reportEndDate, sortBy, sortDirection]);

    /**
     * Drop selected reports that are no longer present in the current view.
     * Mirrors the legacy ReportsTable cleanup so a category/partner switch
     * can't leave stale selections in the batch-download payload.
     * Bails out early if nothing changed to avoid an extra render.
     */
    useEffect(() => {
        setSelectedReports((prev) => {
            const next = prev.filter((sel) => recentReports.find((r) => r.id === sel.id));
            return next.length === prev.length ? prev : next;
        });
    }, [recentReports]);

    // ====== EARLY RETURNS / ROUTE GUARDS ======
    // Wait for partnerAccess to load before rendering anything
    if (!user?.partnerAccess || !hasCheckedAuthentication) {
        return <Spinner active={true} />;
    }

    // If a partner is resolved but isn't in the user's accessible list, swap to
    // the access-denied screen instead of rendering the page.
    if (currentPartnerCode && !availablePartners.find((p) => p.code === currentPartnerCode)) {
        return <NotAuthorizedPage />;
    }

    // Show error state if user has no partners assigned
    if (availablePartners.length === 0) {
        return (
            <div className="home-page-v2">
                <div className="analytics-header">
                    {isDesktop && <div className="analytics-icon-wrapper"><img src={analyticsIcon} alt="Analytics Icon" className="analytics-icon" /></div>}
                    <h2 className="analytics-title">Analytics</h2>
                </div>
                <div className="no-partners-page">
                    <h2>No Partners Available</h2>
                    <p>
                        Your account does not currently have access to any partners.
                        If you believe this is an error, please contact your administrator.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="home-page-v2">
            {/* Analytics Header */}
            {/* <div className="analytics-header">
                {isDesktop && <div className="analytics-icon-wrapper"><img src={analyticsIcon} alt="Analytics Icon" className="analytics-icon" /></div>}
                <h2 className="analytics-title">Analytics</h2>
            </div> */}

            {/* Partner Header with switcher */}
            <div className="partner-header" style={{ backgroundColor: partnerColor }}>
                <div className="partner-header-content">
                    {selectedPartner && (
                        <>
                            <div className="partner-logo-wrapper">
                                <CardLogo
                                    item={{ name: selectedPartner.name, code: selectedPartner.code }}
                                    defaultIconType="HomeLined"
                                />
                            </div>
                            {availablePartners.length > 1 && (
                                <button
                                    className="partner-switch-button"
                                    onClick={() => setShowPartnerModal(true)}
                                    aria-label="Switch partner"
                                >
                                    <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M7 10L12 15L17 10H7Z"
                                            fill="white"
                                        />
                                    </svg>
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Partner Switch Modal */}
            <PartnerSwitchModal
                isOpen={showPartnerModal}
                onClose={() => setShowPartnerModal(false)}
                accessTypes={user.partnerAccess.accessTypes}
                partners={user.partnerAccess.partners}
                onPartnerSelect={handlePartnerSwitch}
            />

            {/* Dashboards Section */}
            {isLoading && hasAccessToDashboards && <Spinner active={true} />}
            {!isLoading && hasAccessToDashboards && (
                <section className="dashboards-section">
                    <div className="section-header dashboards-section-header">
                        <h3>Dashboards</h3>
                        {dashboards.length > 1 && (
                            <DropdownSelect
                                label="Sort by:"
                                options={SORT_OPTIONS}
                                value={sortOption}
                                onChange={handleSort}
                            />
                        )}
                       
                    </div>

                    <div className="section-content">

                        <div className="dashboards-grid-wrapper">
                            {sortLoading && <Spinner active={true} />}
                            <div
                                className={`dashboards-grid ${isDesktop ? 'dashboards-grid-desktop' : 'dashboards-grid-mobile'}`}
                                style={{
                                    opacity: sortLoading ? 0.3 : 1,
                                    transition: 'opacity 0.3s ease',
                                }}
                            >
                                {dashboards.length === 0 ? (
                                    <div className="no-dashboards-message">
                                        There are no dashboards available for {partnerName}.
                                    </div>
                                ) : (
                                    visibleDashboards.map((dashboard) => (
                                        <DashboardCard
                                            key={dashboard.dashboardId}
                                            id={`dashboard-${dashboard.dashboardId}`}
                                            title={dashboard.dashboardName}
                                            description={dashboard.dashboardDescription}
                                            onClick={(e) => { e.preventDefault(); handleDashboardClick(dashboard); }}
                                        />
                                    ))
                                )}
                            </div>
                        </div>

                        {!sortLoading && dashboards.length > currentDashboardLimit && (
                            <div className="view-more-link" onClick={() => setShowAllDashboards(!showAllDashboards)}>
                                <dxo-ui-button
                                    style-variant="grv-button primary"
                                    id="view-more-button"
                                    name="view-more-dashboards"
                                    text={showAllDashboards ? 'View less' : 'View more'}
                                    type="button"
                                />
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Reports Section - Desktop only */}
            {isDesktop && isLoadingReports && hasAccessToReports && partnerHasReports && <Spinner active={true} />}
            {isDesktop && !isLoadingReports && hasAccessToReports && partnerHasReports && (
                <section className="reports-section">
                    <div className="section-header reports-section-header">
                        <div className="reports-header-row">
                            <h3>Reports</h3>
                        </div>
                        {/* <p>Find reports by date and type, then select to download for analysis.</p> */}
                    </div>

                    <div className="section-content">
                        {reportCategories.length === 0 ? (
                            <div className="no-dashboards-message">
                                {reportCategoriesError
                                    ? `Unable to load reports for ${partnerName}.`
                                    : `There are no report categories available for ${partnerName}.`
                                }
                            </div>
                        ) : (
                            <>
                                <div className="reports-filters-row">
                                    <div className="report-category-dropdown-container">
                                        <DropdownSelect
                                            label="Report Category"
                                            options={[
                                                { label: "All", value: ALL_CATEGORIES_CODE },
                                                ...reportCategories.map((category) => ({
                                                    label: category.name,
                                                    value: category.code,
                                                })),
                                            ]}
                                            value={selectedCategoryCode}
                                            onChange={(newCode) => {
                                                setSelectedCategoryCode(newCode);
                                                // Reset the date filter so the date-init effect re-picks the
                                                // latest date for the new category. Without this, a date locked
                                                // in by a previous category can hide reports whose latest is
                                                // older than that locked date.
                                                setReportStartDate(null);
                                                setReportEndDate(null);
                                            }}
                                        />
                                    </div>
                                    {selectedCategoryCode !== ALL_CATEGORIES_CODE && allReports.length > 0 && reportStartDate && reportEndDate && (
                                        <div className="reports-date-selector-container">
                                            <ReportsDateSelector
                                                reports={allReports}
                                                startValue={reportStartDate}
                                                endValue={reportEndDate}
                                                onStartChange={setReportStartDate}
                                                onEndChange={setReportEndDate}
                                            />
                                        </div>
                                    )}
                                    {/* Batch download disabled until reCAPTCHA allowlist is fixed in QA/prod.
                                        Re-enable by uncommenting this block (and the Select column below).
                                    {recentReports.length > 0 && (() => {
                                        const isInternalUser = user?.isExternalUser != null && !user.isExternalUser;
                                        const downloadButton = (
                                            <Button
                                                htmlButtonType="submit"
                                                id="download-selected-reports"
                                                type="progressive"
                                                loading={isDownloadInProgress}
                                                disabledOnLoading
                                                disabled={selectedReports.length === 0}
                                            >
                                                {`Download${selectedReports.length ? ` (${selectedReports.length})` : ''}`}
                                            </Button>
                                        );
                                        return (
                                            <div className="reports-download-action">
                                                {isInternalUser ? (
                                                    <Form onSubmit={downloadSelectedReports} id="downloadReportsV2" name="downloadReports">
                                                        {downloadButton}
                                                    </Form>
                                                ) : (
                                                    <FormWithReCaptcha onSubmit={downloadSelectedReports} id="downloadReportsV2" name="downloadReports">
                                                        {downloadButton}
                                                    </FormWithReCaptcha>
                                                )}
                                            </div>
                                        );
                                    })()}
                                    */}
                                </div>

                                {(recentReports.length > 0 || reportsTableLoading) && (
                                    <div className="reports-table-container">
                                        {/* Pairs with disabled batch-download form above.
                                        {downloadError && (
                                            <div className="reports-download-error" role="alert">{downloadError}</div>
                                        )}
                                        */}
                                        <div className="reports-table-wrapper">
                                            {reportsTableLoading && <Spinner active={true} />}
                                            <table
                                                className="reports-table"
                                                style={{
                                                    opacity: reportsTableLoading ? 0.3 : 1,
                                                    transition: 'opacity 0.3s ease',
                                                }}
                                            >
                                                <thead>
                                                    <tr>
                                                        {/* Select column disabled until reCAPTCHA allowlist is fixed.
                                                            Re-enable along with the batch-download form above. */}
                                                        {/* <th className="reports-table-select-cell">Select</th> */}
                                                        <th>
                                                            <SortableColumnHeader<DisplayReport>
                                                                title="Report Date"
                                                                dataIndex="reportDate"
                                                                sortIndex="reportDate"
                                                                sortBy={sortBy}
                                                                sortDirection={sortDirection}
                                                                setSortBy={setSortBy}
                                                                setSortDirection={setSortDirection}
                                                            />
                                                        </th>
                                                        <th>
                                                            <SortableColumnHeader<DisplayReport>
                                                                title="File Name"
                                                                dataIndex="fileName"
                                                                sortIndex="fileName"
                                                                sortBy={sortBy}
                                                                sortDirection={sortDirection}
                                                                setSortBy={setSortBy}
                                                                setSortDirection={setSortDirection}
                                                            />
                                                        </th>
                                                        <th>
                                                            {/* Sort on rawSize so "1.2 MB" doesn't sort as a string ahead of "245 KB". */}
                                                            <SortableColumnHeader<DisplayReport>
                                                                title="File Size"
                                                                dataIndex="rawSize"
                                                                sortIndex="rawSize"
                                                                sortBy={sortBy}
                                                                sortDirection={sortDirection}
                                                                setSortBy={setSortBy}
                                                                setSortDirection={setSortDirection}
                                                            />
                                                        </th>
                                                        <th>
                                                            <SortableColumnHeader<DisplayReport>
                                                                title="Last Modified"
                                                                dataIndex="lastModified"
                                                                sortIndex="lastModified"
                                                                sortBy={sortBy}
                                                                sortDirection={sortDirection}
                                                                setSortBy={setSortBy}
                                                                setSortDirection={setSortDirection}
                                                            />
                                                        </th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="reports-tbody-wrapper">
                                                    {recentReports.map((report) => {
                                                        return (
                                                            <tr key={report.id}>
                                                                {/* Select cell disabled until reCAPTCHA allowlist is fixed.
                                                                    Re-enable along with the batch-download form above.
                                                                <td className="reports-table-select-cell">
                                                                    <Checkbox
                                                                        checked={isReportSelected(report)}
                                                                        label={`Select ${report.fileName}`}
                                                                        onClick={() => toggleReportSelected(report)}
                                                                        disabled={isMaxNumberOfReportsSelected && !isReportSelected(report)}
                                                                        hideLabel
                                                                    />
                                                                </td>
                                                                */}
                                                                <td>{formatDate(report.reportDate)}</td>
                                                                <td>
                                                                    <button
                                                                        className="report-filename-button"
                                                                        type="button"
                                                                        onClick={() => { window.scrollTo(0, 0); setViewingReport(report); }}
                                                                    >
                                                                        {report.fileName}
                                                                    </button>
                                                                </td>
                                                                <td>{report.fileSize}</td>
                                                                <td>{formatDate(report.lastModified)}</td>
                                                                <td>
                                                                    <div className="report-download-button" onClick={() => handleDownloadReport(report.id)}>
                                                                        <dxo-ui-button
                                                                            style-variant="grv-button tertiary"
                                                                            id={`download-report-${report.id}`}
                                                                            name={`download-report-${report.id}`}
                                                                            text="Download"
                                                                            type="button"
                                                                        />
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {recentReports.length === 0 && !reportsError && !reportsTableLoading && (
                                    <div className="no-dashboards-message">
                                        {selectedCategoryCode === ALL_CATEGORIES_CODE
                                            ? 'No reports available for any category.'
                                            : 'No reports available in this category.'}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </section>
            )}

            {/* Dashboard Modal for non-Quicksight dashboards */}
            {modalDashboard && (
                <DashboardModal
                    onClose={() => setModalDashboard(null)}
                    dashboard={modalDashboard}
                />
            )}

            {/* View Report Modal */}
            {viewingReport && (
                <ViewReportModal
                    onClose={() => setViewingReport(null)}
                    report={viewingReport}
                />
            )}
        </div>
    );
}

export default HomePageV2;
